import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Event as EventIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  CalendarToday as CalendarIcon,
  Menu as MenuIcon
} from '@mui/icons-material';

const StudyCalendar = ({ studyPlan, onDateClick, onDownloadICS }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Generate calendar days
  const generateCalendarDays = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    while (currentDate <= lastDay || days.length < 42) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const calendarDays = generateCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth());

  // Check if date has study events
  const hasStudyEvent = (date) => {
    if (!studyPlan?.schedule) return false;
    
    const dayOfWeek = date.getDay();
    const dayName = daysOfWeek[dayOfWeek];
    
    return studyPlan.schedule.some(timeSlot => 
      timeSlot.days[dayOfWeek]?.subject
    );
  };

  // Get study events for a specific date
  const getStudyEvents = (date) => {
    if (!studyPlan?.schedule) return [];
    
    const dayOfWeek = date.getDay();
    const events = [];
    
    studyPlan.schedule.forEach(timeSlot => {
      const dayEvent = timeSlot.days[dayOfWeek];
      if (dayEvent?.subject) {
        events.push({
          time: timeSlot.time,
          subject: dayEvent.subject,
          activity: dayEvent.activity,
          color: dayEvent.color
        });
      }
    });
    
    return events;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is in the past
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Check if date is in the future
  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  // Go to today
  const goToToday = () => {
    setCurrentMonth(new Date());
    setSelectedDate(new Date());
  };

  // Handle date click
  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowEventModal(true);
    if (onDateClick) {
      onDateClick(date, getStudyEvents(date));
    }
  };

  // Get motivational message
  const getMotivationalMessage = (date) => {
    const messages = [
      "Every study session brings you closer to your goals! 📚",
      "You're doing great! Keep up the amazing work! 🌟",
      "Small progress is still progress. You've got this! 💪",
      "Today is a perfect day to learn something new! 🎯",
      "Your future self will thank you for studying today! 🚀",
      "Focus on progress, not perfection! 📈",
      "You are capable of amazing things! Believe in yourself! ✨",
      "Every expert was once a beginner. Keep going! 🎓"
    ];
    
    const dayOfYear = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    return messages[dayOfYear % messages.length];
  };

  // Get exam countdown
  const getExamCountdown = () => {
    if (!studyPlan?.endDate) return null;
    
    const examDate = new Date(studyPlan.endDate);
    const today = new Date();
    const diffTime = examDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      return diffDays;
    }
    return null;
  };

  const examCountdown = getExamCountdown();

  return (
    <div className="w-full max-w-6xl mx-auto flex gap-6">
      {/* Main Calendar */}
      <div className="w-4/5 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Ultra Compact Header */}
        <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white p-3">
          {/* Single Row: Title, Navigation, and Actions */}
          <div className="flex items-center justify-between">
            {/* Left: Title */}
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold">
                📅 Study Calendar
              </h2>
              {examCountdown && (
                <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded px-2 py-0.5">
                  <div className="text-xs font-medium">
                    ⏰ {examCountdown} days
                  </div>
                </div>
              )}
            </div>
            
            {/* Center: Month Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousMonth}
                className="p-1 bg-white/20 backdrop-blur-sm border border-white/30 rounded hover:bg-white/30 transition-all duration-200 hover:scale-105"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              
              <div className="text-center">
                <h3 className="text-sm font-bold">
                  {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h3>
              </div>
              
              <button
                onClick={goToNextMonth}
                className="p-1 bg-white/20 backdrop-blur-sm border border-white/30 rounded hover:bg-white/30 transition-all duration-200 hover:scale-105"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
            
            {/* Right: Action Buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={onDownloadICS}
                className="flex items-center gap-1 px-2 py-1 bg-white/20 backdrop-blur-sm border border-white/30 rounded hover:bg-white/30 transition-all duration-200 text-xs font-medium"
              >
                <DownloadIcon className="w-3 h-3" />
                Export
              </button>
              
              <button
                onClick={goToToday}
                className="flex items-center gap-1 px-2 py-1 bg-white/20 backdrop-blur-sm border border-white/30 rounded hover:bg-white/30 transition-all duration-200 text-xs font-medium"
              >
                <TodayIcon className="w-3 h-3" />
                Today
              </button>
            </div>
          </div>
        </div>

        {/* Compact Calendar Grid */}
        <div className="p-6">
          {/* Days of Week Header */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {daysOfWeek.map(day => (
              <div
                key={day}
                className="p-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((date, index) => {
              const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
              const hasEvent = hasStudyEvent(date);
              const isTodayDate = isToday(date);
              const isPast = isPastDate(date);
              const isFuture = isFutureDate(date);
              
              return (
                <motion.button
                  key={index}
                  onClick={() => handleDateClick(date)}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  className={`
                    relative p-2 h-16 rounded-lg border transition-all duration-300 text-left group
                    ${isCurrentMonth 
                      ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md' 
                      : 'bg-gray-50 dark:bg-gray-900/50 border-gray-100 dark:border-gray-800 text-gray-400 dark:text-gray-600'
                    }
                    ${isTodayDate ? 'ring-2 ring-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-300 dark:border-blue-600' : ''}
                    ${hasEvent ? 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-300 dark:border-green-600' : ''}
                    ${isPast ? 'opacity-50' : ''}
                    ${isFuture ? 'hover:shadow-lg' : ''}
                  `}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`
                      text-sm font-semibold
                      ${isTodayDate ? 'text-blue-600 dark:text-blue-400' : ''}
                      ${hasEvent ? 'text-green-600 dark:text-green-400' : ''}
                      ${!isCurrentMonth ? 'text-gray-400 dark:text-gray-600' : 'text-gray-800 dark:text-gray-200'}
                    `}>
                      {date.getDate()}
                    </span>
                    
                    {hasEvent && (
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    )}
                  </div>
                  
                  {hasEvent && (
                    <div className="space-y-0.5">
                      <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                        📚 Study
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {getStudyEvents(date).length} session{getStudyEvents(date).length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  )}
                  
                  {isTodayDate && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    </div>
                  )}
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend Ribbon Card */}
      <div className="w-1/5 flex-shrink-0">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 h-fit sticky top-6">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
            📅 Legend
          </h4>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-xs text-gray-600 dark:text-gray-400">Today</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-xs text-gray-600 dark:text-gray-400">Study Day</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
              <span className="text-xs text-gray-600 dark:text-gray-400">Other Month</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span className="text-xs text-gray-600 dark:text-gray-400">Events</span>
            </div>
          </div>
        </div>
      </div>

      {/* Event Creation Modal */}
      <AnimatePresence>
        {showEventModal && selectedDate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEventModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4 mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                    <MenuIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Add title and time
                  </h3>
                </div>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>

              {/* Title Input */}
              <div className="mb-6">
                <input
                  type="text"
                  placeholder="Add title"
                  className="w-full text-xl font-medium border-b-2 border-gray-300 dark:border-gray-600 bg-transparent text-gray-800 dark:text-gray-200 focus:border-blue-500 dark:focus:border-blue-400 outline-none pb-2"
                />
              </div>

              {/* Type Selector */}
              <div className="flex gap-1 mb-6">
                <button className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium">
                  Event
                </button>
                <button className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium">
                  Task
                </button>
              </div>

                             {/* Event Details */}
               <div className="space-y-3">
                 {/* Time/Date */}
                 <div className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer">
                   <div className="flex items-center gap-2 min-w-0 flex-1">
                     <ScheduleIcon className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                     <div className="min-w-0 flex-1">
                       <div className="text-xs text-gray-800 dark:text-gray-200 truncate">
                         {selectedDate.toLocaleDateString('en-US', { 
                           weekday: 'short', 
                           month: 'short', 
                           day: 'numeric',
                           year: 'numeric'
                         })}
                       </div>
                       <div className="text-xs text-gray-500 dark:text-gray-400">
                         Does not repeat
                       </div>
                     </div>
                   </div>
                   <button className="text-blue-500 text-xs font-medium flex-shrink-0 ml-2">
                     Add time
                   </button>
                 </div>

                                 {/* Subject */}
                 <div className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer">
                   <div className="flex items-center gap-2 min-w-0 flex-1">
                     <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                       <span className="text-white text-xs">📚</span>
                     </div>
                     <span className="text-xs text-gray-800 dark:text-gray-200 truncate">Add subject</span>
                   </div>
                   <button className="text-blue-500 text-xs font-medium flex-shrink-0 ml-2">
                     Add
                   </button>
                 </div>

                 {/* Study Type */}
                 <div className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer">
                   <div className="flex items-center gap-2 min-w-0 flex-1">
                     <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                       <span className="text-white text-xs">🎯</span>
                     </div>
                     <span className="text-xs text-gray-800 dark:text-gray-200 truncate">Add study type</span>
                   </div>
                   <button className="text-blue-500 text-xs font-medium flex-shrink-0 ml-2">
                     Add
                   </button>
                 </div>

                 {/* Description */}
                 <div className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer">
                   <div className="flex items-center gap-2 min-w-0 flex-1">
                     <div className="w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                       <span className="text-white text-xs">📝</span>
                     </div>
                     <span className="text-xs text-gray-800 dark:text-gray-200 truncate">Add description</span>
                   </div>
                   <button className="text-blue-500 text-xs font-medium flex-shrink-0 ml-2">
                     Add
                   </button>
                 </div>

                 {/* Attachments */}
                 <div className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer">
                   <div className="flex items-center gap-2 min-w-0 flex-1">
                     <div className="w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                       <span className="text-white text-xs">📎</span>
                     </div>
                     <span className="text-xs text-gray-800 dark:text-gray-200 truncate">Add attachment</span>
                   </div>
                   <button className="text-blue-500 text-xs font-medium flex-shrink-0 ml-2">
                     Add
                   </button>
                 </div>

                 {/* Calendar */}
                 <div className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer">
                   <div className="flex items-center gap-2 min-w-0 flex-1">
                     <CalendarIcon className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                     <div className="min-w-0 flex-1">
                       <div className="text-xs text-gray-800 dark:text-gray-200 flex items-center gap-1">
                         Study Calendar
                         <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                       </div>
                       <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                         Free • Default visibility • 2 notifications
                       </div>
                     </div>
                   </div>
                   <button className="text-blue-500 text-xs font-medium flex-shrink-0 ml-2">
                     Change
                   </button>
                 </div>
              </div>

              {/* Footer Buttons */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button className="text-gray-600 dark:text-gray-400 text-sm font-medium">
                  More options
                </button>
                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => setShowEventModal(false)}
                    className="flex-1 sm:flex-none px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200 text-sm"
                  >
                    Cancel
                  </button>
                  <button className="flex-1 sm:flex-none px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 font-medium text-sm">
                    Save
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudyCalendar; 